/*
 * article-toc.gen2.js — TOC scrollspy + reading progress for article-v2
 * - Auto-extracts H2 / H3 from .article-body .rte
 * - Renders into [data-toc-target] and [data-toc-mobile-target]
 * - Active highlight via IntersectionObserver
 * - Reading progress bar updated via scroll
 * Respects prefers-reduced-motion: still updates state, skips smooth scroll.
 */

(function () {
  'use strict';

  const root = document.querySelector('.article-v2');
  if (!root) return;

  const body = root.querySelector('.article-body .rte');
  const tocDesktop = root.querySelector('[data-toc-target]');
  const tocMobile = root.querySelector('[data-toc-mobile-target]');
  const progressFill = root.querySelector('[data-progress-fill]');
  if (!body) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // 1. Find all H2 / H3 in article body, assign IDs if missing
  const headings = Array.from(body.querySelectorAll('h2, h3'));
  if (!headings.length) {
    if (tocDesktop) tocDesktop.closest('.article-rail__toc-block')?.remove();
    if (tocMobile) tocMobile.closest('.article-toc-mobile')?.remove();
  } else {
    const idsSeen = new Set();
    headings.forEach((h, i) => {
      if (h.id) {
        idsSeen.add(h.id);
        return;
      }
      // Unicode-aware slug: keep all letters/numbers across scripts (DE/ES/FR/IT/VI/CJK).
      let slug = (h.textContent || '')
        .trim()
        .toLowerCase()
        .replace(/[^\p{L}\p{N}\s-]/gu, '')
        .replace(/\s+/g, '-')
        .slice(0, 60) || `section-${i}`;
      // Dedupe collisions (two H2 with same text would otherwise produce duplicate IDs).
      if (idsSeen.has(slug)) {
        let n = 2;
        while (idsSeen.has(`${slug}-${n}`)) n += 1;
        slug = `${slug}-${n}`;
      }
      idsSeen.add(slug);
      h.id = slug;
    });

    // 2. Build TOC list HTML
    const buildList = () => {
      const ul = document.createElement('ul');
      ul.className = 'article-toc__list';
      headings.forEach((h) => {
        const li = document.createElement('li');
        li.className = `article-toc__item article-toc__item--${h.tagName.toLowerCase()}`;
        const a = document.createElement('a');
        a.href = `#${h.id}`;
        a.className = 'article-toc__link';
        a.textContent = h.textContent || '';
        a.dataset.tocLink = h.id;
        li.appendChild(a);
        ul.appendChild(li);
      });
      return ul;
    };

    if (tocDesktop) tocDesktop.appendChild(buildList());
    if (tocMobile) tocMobile.appendChild(buildList());

    // 3. Smooth scroll on TOC click
    root.querySelectorAll('[data-toc-link]').forEach((link) => {
      link.addEventListener('click', (e) => {
        const id = link.getAttribute('data-toc-link');
        const target = document.getElementById(id);
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({
          behavior: reducedMotion ? 'auto' : 'smooth',
          block: 'start',
        });
        history.replaceState(null, '', `#${id}`);
        // close mobile details after click
        const details = link.closest('details');
        if (details && details.open) details.open = false;
      });
    });

    // 4. Scrollspy via IntersectionObserver
    const links = Array.from(root.querySelectorAll('[data-toc-link]'));
    const linkById = new Map(links.map((l) => [l.dataset.tocLink, l]));
    let activeId = null;

    const setActive = (id) => {
      if (activeId === id) return;
      activeId = id;
      links.forEach((l) => l.classList.remove('is-active'));
      const link = linkById.get(id);
      if (link) link.classList.add('is-active');
    };

    const observer = new IntersectionObserver(
      (entries) => {
        // Find topmost intersecting heading; if none, find last passed
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) {
          setActive(visible[0].target.id);
        }
      },
      {
        rootMargin: '-15% 0px -70% 0px',
        threshold: 0,
      }
    );
    headings.forEach((h) => observer.observe(h));

    // initial
    if (headings[0]) setActive(headings[0].id);
  }

  // 5. Reading progress bar
  if (progressFill) {
    let ticking = false;
    const update = () => {
      const rect = body.getBoundingClientRect();
      const total = body.offsetHeight - window.innerHeight;
      const scrolled = Math.max(0, -rect.top);
      const pct = total > 0 ? Math.min(100, (scrolled / total) * 100) : 0;
      progressFill.style.setProperty('--progress', `${pct}%`);
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }
})();
